/**
 * 竞品透视页面 — Competitive（UI 重写，对齐 competitive.html）。
 *
 * GET /api/competitive 获取竞品对比列表（gap_matrix 解析逻辑 100% 保留）。
 * GET /api/competitive/moves 竞品异动（批 4 §7，与预警引擎 AR3 同源；未就绪 EmptyState 降级）。
 *
 * 新 UI：BAVS 综合分指标卡（我方 + 各竞品）→ 五维对比条（.cmp-row 式）
 * → 差距最大维度高亮（warning 浅底行）→ 口径注 → 异动检测卡。
 *
 * 展示门禁（指标字典 §13.3，批 7 收尾）：
 * - 我方/竞品综合分算不出（gap_matrix 无五维数组）→ 该卡不渲染；竞品卡
 *   回退真实 ai_visibility_rate（标注口径），两者皆无也不渲染，禁止"待产出"；
 * - 五维对比只渲染任一方有真实分数的维度；全无可比维度 → EmptyState 正常空态；
 * - 候选竞品区（聚合/确认动作二期接入，整块占位无数据）→ 已移除不渲染。
 *
 * 数据真实性铁律：
 * - weaknesses 存的是 BAVS 维度名而非业务劣势 → 解析为对比维度清单
 * - gap_matrix 存有 BAVS 各维度 target_score（我方）和 competitor_scores（竞品）→ 展示真实数值
 * - 测试数据批 1 已由后端 is_test 过滤，前端不再裸露"测试数据"标注
 */
import { useEffect, useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
} from "@mui/material";
import PageHeader from "../components/PageHeader";
import Loading from "../components/Loading";
import EmptyState from "../components/EmptyState";
import UpdateCapsule from "../components/UpdateCapsule";
import Badge from "../components/Badge";
import {
  getCompetitiveComparison,
  getCompetitiveMoves,
  getCompetitiveSnapshot,
  getPositionMetrics,
} from "../api/competitive";
import { useCompanyStore } from "../store/companyStore";
import { formatDateTime } from "../utils/format";
import type {
  CompetitorComparison,
  CompetitiveMove,
  CompetitiveSnapshot,
  PositionMetrics,
} from "../types";

/** BAVS 五维标准维度名（洞悉引擎五维）。 */
const BAVS_DIMENSIONS: readonly string[] = [
  "品牌提及率",
  "答案份额",
  "信源引用率",
  "情感占比",
  "排名位置",
];

/** 异动指标 key → 中文名（metric 原始英文键不直出）。 */
const MOVE_METRIC_LABELS: Record<string, string> = {
  mention_rate: "提及率",
  ranking: "排名",
};

/** 格式化异动数值（0–100 量纲，去除多余小数位）。 */
function formatMoveValue(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

/** 位次/情感指标格式化（P1#21/#22）：null → 「—」（无数据如实占位，严禁虚构）。 */
function formatPct(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return `${v}%`;
}

function formatRank(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return v.toFixed(1);
}

/** gap_matrix 单条维度差距数据（skill 产出）。 */
interface GapMatrixEntry {
  dimension: string;
  target_score: number;
  competitor_scores: Record<string, number>;
  gaps: Record<string, number>;
}

/**
 * 解析 gap_matrix 为维度名 → 条目的映射。
 * gap_matrix 可能为：数组（skill 产出）或 null/旧格式对象（已清理）。
 */
function parseGapMatrix(gapMatrix: unknown): Map<string, GapMatrixEntry> {
  const map = new Map<string, GapMatrixEntry>();
  if (Array.isArray(gapMatrix)) {
    for (const entry of gapMatrix) {
      if (
        entry &&
        typeof entry === "object" &&
        typeof (entry as GapMatrixEntry).dimension === "string"
      ) {
        const e = entry as GapMatrixEntry;
        map.set(e.dimension, e);
      }
    }
  }
  return map;
}

/** 格式化 BAVS 维度分数（去除多余小数位）。 */
function formatScore(score: number | null | undefined): string {
  if (score === null || score === undefined) return "—";
  return Number.isInteger(score) ? String(score) : score.toFixed(1);
}

/** BAVS 五维权重（%），综合分 = Σ(维度分×权重)/Σ(有数据维度权重)。 */
const BAVS_WEIGHTS: Record<string, number> = {
  品牌提及率: 25,
  答案份额: 25,
  信源引用率: 20,
  情感占比: 15,
  排名位置: 15,
};

/** 对比条配色：我方 primary.main，竞品依次 grey.400/success.main/info.main/warning.main。 */
const RIVAL_BAR_COLORS = ["grey.400", "success.main", "info.main", "warning.main"];

/** BAVS 加权综合分（仅统计有数据的标准五维，无数据维度跳过；全缺 → null）。 */
function weightedComposite(
  dims: string[],
  scores: (number | null | undefined)[],
): number | null {
  let sum = 0;
  let wsum = 0;
  dims.forEach((d, i) => {
    const w = BAVS_WEIGHTS[d];
    const s = scores[i];
    if (w && s !== null && s !== undefined) {
      sum += s * w;
      wsum += w;
    }
  });
  return wsum > 0 ? sum / wsum : null;
}

/** 五维对比条（.cmp-row 式：名称 120px + 轨道 + 数值 64px）。 */
function CompareBar({
  name,
  score,
  color,
}: {
  name: string;
  score: number | null | undefined;
  color: string;
}) {
  const pct = score !== null && score !== undefined ? Math.max(0, Math.min(100, score)) : 0;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "120px 1fr 64px",
        alignItems: "center",
        gap: 1.5,
        py: 1,
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          color: "text.secondary",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </Typography>
      <Box
        sx={{
          height: 10,
          borderRadius: 5,
          bgcolor: "semanticBg.neutral",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${pct}%`,
            borderRadius: 5,
            bgcolor: color,
          }}
        />
      </Box>
      <Typography sx={{ fontSize: 13, fontWeight: 600, textAlign: "right" }}>
        {formatScore(score)}
      </Typography>
    </Box>
  );
}

export default function Competitive() {
  const selectedBrandId = useCompanyStore((s) => s.selectedBrandId);
  const [items, setItems] = useState<CompetitorComparison[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // 批 4：竞品异动（moves 端点未就绪一律降级，不红屏）
  const [moves, setMoves] = useState<CompetitiveMove[]>([]);
  const [movesFailed, setMovesFailed] = useState(false);
  const [snapshot, setSnapshot] = useState<CompetitiveSnapshot | null>(null);

  // P1#21/#22：Top1/Top3 占位率 + 情感占比（端点异常 → null 不渲染该卡）
  const [posMetrics, setPosMetrics] = useState<PositionMetrics | null>(null);

  useEffect(() => {
    fetchCompetitive();
  }, [selectedBrandId]);

  useEffect(() => {
    if (!selectedBrandId) {
      setPosMetrics(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const m = await getPositionMetrics(selectedBrandId);
        if (!cancelled) setPosMetrics(m);
      } catch {
        if (!cancelled) setPosMetrics(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedBrandId]);

  const fetchCompetitive = async (): Promise<void> => {
    setLoading(true);
    setError("");
    try {
      const result = await getCompetitiveComparison(selectedBrandId || undefined);
      setItems(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "获取竞品对比数据失败");
    } finally {
      setLoading(false);
    }
  };

  /** 竞品异动（AR3 同源；失败置降级标记）。
      顺带取竞品基线快照：异动为空（单期运行）时降级展示快照，不留空壳。 */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getCompetitiveMoves(selectedBrandId || undefined, 7);
        if (!cancelled) {
          setMoves(result.items || []);
          setMovesFailed(false);
        }
      } catch {
        if (!cancelled) {
          setMoves([]);
          setMovesFailed(true);
        }
      }
      try {
        const snap = await getCompetitiveSnapshot(
          selectedBrandId || undefined,
          30,
        );
        if (!cancelled) setSnapshot(snap);
      } catch {
        if (!cancelled) setSnapshot(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedBrandId]);

  /**
   * 从所有竞品的 weaknesses 数组解析出对比维度清单。
   * 以 BAVS 五维为基准，合并 weaknesses 中出现的额外维度名。
   * weaknesses 在 skill 语境下存的是 BAVS 维度名（非业务劣势描述）。
   */
  const comparisonDimensions: string[] = useMemo(() => {
    const dimSet = new Set<string>(BAVS_DIMENSIONS);
    for (const item of items) {
      if (item.weaknesses && Array.isArray(item.weaknesses)) {
        for (const w of item.weaknesses) {
          if (typeof w === "string" && w.trim()) {
            dimSet.add(w.trim());
          }
        }
      }
    }
    return Array.from(dimSet);
  }, [items]);

  /**
   * 为每个竞品解析 gap_matrix，构建维度 → 分数查找表。
   * key = competitor item id，value = Map<dimension, GapMatrixEntry>
   */
  const gapMatrixByItem: Map<string, Map<string, GapMatrixEntry>> = useMemo(() => {
    const result = new Map<string, Map<string, GapMatrixEntry>>();
    for (const item of items) {
      result.set(item.id, parseGapMatrix(item.gap_matrix));
    }
    return result;
  }, [items]);

  /**
   * 我方品牌各维度 target_score 查找表。
   * 取所有竞品 gap_matrix 中该维度 target_score 的最大值（取最优记录）。
   * 不同竞品可能引用不同时间点的我方数据，取最大值更稳妥。
   */
  const ourScores: Map<string, number> = useMemo(() => {
    const result = new Map<string, number>();
    for (const dim of comparisonDimensions) {
      let bestScore: number | null = null;
      for (const item of items) {
        const gm = gapMatrixByItem.get(item.id);
        const entry = gm?.get(dim);
        if (entry && typeof entry.target_score === "number") {
          if (bestScore === null || entry.target_score > bestScore) {
            bestScore = entry.target_score;
          }
        }
      }
      if (bestScore !== null) {
        result.set(dim, bestScore);
      }
    }
    return result;
  }, [items, comparisonDimensions, gapMatrixByItem]);

  /**
   * 差距最大维度（PRD §2.3：补"差距最大维度"高亮）。
   * 遍历各维度，算我方 vs 各竞品的最大绝对差距，取差距最大的维度高亮。
   */
  const maxGapDimension: string | null = useMemo(() => {
    let maxGap = 0;
    let maxDim: string | null = null;
    for (const dim of comparisonDimensions) {
      const ourScore = ourScores.get(dim);
      if (ourScore === undefined) continue;
      for (const item of items) {
        const gm = gapMatrixByItem.get(item.id);
        const entry = gm?.get(dim);
        const competitorScore =
          entry?.competitor_scores?.[item.competitor_name];
        if (competitorScore !== undefined && competitorScore !== null) {
          const gap = Math.abs(ourScore - competitorScore);
          if (gap > maxGap) {
            maxGap = gap;
            maxDim = dim;
          }
        }
      }
    }
    return maxDim;
  }, [items, comparisonDimensions, gapMatrixByItem, ourScores]);

  /**
   * 实际渲染的对比维度（门禁 §13.3）：只保留我方或任一竞品有真实分数的维度；
   * 双方都无数据的维度整行不渲染，禁止空轨道/"—"占位伪装。
   */
  const visibleDimensions: string[] = useMemo(
    () =>
      comparisonDimensions.filter((dim) => {
        if (ourScores.get(dim) !== undefined) return true;
        return items.some((item) => {
          const entry = gapMatrixByItem.get(item.id)?.get(dim);
          const s = entry?.competitor_scores?.[item.competitor_name];
          return s !== undefined && s !== null;
        });
      }),
    [items, comparisonDimensions, gapMatrixByItem, ourScores],
  );

  /** 最近记录时间（更新胶囊用，取各竞品 recorded_at 最大值）。 */
  const latestRecordedAt: string | null = useMemo(() => {
    if (items.length === 0) return null;
    return items.reduce(
      (latest, i) => (i.recorded_at > latest ? i.recorded_at : latest),
      items[0].recorded_at,
    );
  }, [items]);

  /** 我方 BAVS 综合分。 */
  const ourComposite = useMemo(
    () =>
      weightedComposite(
        comparisonDimensions,
        comparisonDimensions.map((d) => ourScores.get(d)),
      ),
    [comparisonDimensions, ourScores],
  );

  /** 各竞品 BAVS 综合分（key = item.id）。 */
  const rivalCompositeById: Map<string, number | null> = useMemo(() => {
    const result = new Map<string, number | null>();
    for (const item of items) {
      const gm = gapMatrixByItem.get(item.id);
      result.set(
        item.id,
        weightedComposite(
          comparisonDimensions,
          comparisonDimensions.map(
            (d) => gm?.get(d)?.competitor_scores?.[item.competitor_name],
          ),
        ),
      );
    }
    return result;
  }, [items, comparisonDimensions, gapMatrixByItem]);

  /** 异动级别计数（卡头徽章）。 */
  const moveP0 = moves.filter((m) => m.level === "P0").length;
  const moveP1 = moves.filter((m) => m.level === "P1").length;

  if (loading) {
    return (
      <Box>
        <PageHeader title="竞品透视" />
        <Loading fullPage />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="竞品透视"
        breadcrumbs={[{ label: "GEO 分析" }, { label: "竞品对比" }]}
        subtitle="我方 vs 竞品 BAVS 五维同口径对比 · 异动检测与预警引擎 AR3 同源"
        actions={
          latestRecordedAt ? (
            <UpdateCapsule
              updatedAt={latestRecordedAt}
              sampleN={items.length}
            />
          ) : undefined
        }
      />

      {/* BAVS 综合分指标卡（我方 + 各竞品）
          门禁 §13.3：综合分依赖 gap_matrix 五维数据，算不出（null）时不渲染该卡，
          禁止"待产出"伪装；竞品卡回退展示真实 ai_visibility_rate（如实标注口径），
          两者皆无的竞品卡同样不渲染。 */}
      {!error && items.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {ourComposite !== null && (
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 1 }}>
                    我方品牌 · BAVS 综合分
                  </Typography>
                  <Typography
                    sx={{ fontSize: 24, fontWeight: 600, color: "primary.main" }}
                  >
                    {ourComposite.toFixed(1)}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
                    五维加权（口径见下方注）
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
          {items.map((item) => {
            const composite = rivalCompositeById.get(item.id);
            // H2 修复：gap_matrix 存量为旧排名 dict（无五维数组）时，综合分无法
            // 计算；回退展示该行真实已有的 ai_visibility_rate（如实标注口径）。
            // 门禁 §13.3：综合分与提及率皆无 → 该卡不渲染（禁止"待产出"伪装）。
            const hasComposite = composite !== null && composite !== undefined;
            const hasRate =
              item.ai_visibility_rate !== null &&
              item.ai_visibility_rate !== undefined;
            if (!hasComposite && !hasRate) return null;
            return (
              <Grid item xs={12} sm={6} md={3} key={item.id}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "text.secondary",
                        mb: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.competitor_name} ·{" "}
                      {hasComposite ? "BAVS 综合分" : "AI 可见率"}
                    </Typography>
                    <Typography sx={{ fontSize: 24, fontWeight: 600 }}>
                      {hasComposite
                        ? composite.toFixed(1)
                        : `${formatScore(item.ai_visibility_rate)}%`}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
                      {hasComposite
                        ? `记录于 ${formatDateTime(item.recorded_at).split(" ")[0]}`
                        : `提及率口径 · 五维综合分暂不可算 · ${formatDateTime(item.recorded_at).split(" ")[0]}`}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* P1#21/#22：AI 回答位次与情感（全量探测样本，品牌级聚合）
          门禁：端点异常 → 整卡不渲染；单项指标 null → 「—」（严禁虚构）。 */}
      {posMetrics && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography
              sx={{ fontSize: 14, fontWeight: 500, color: "primary.main", mb: 0.5 }}
            >
              AI 回答位次与情感 · 全量探测样本
            </Typography>
            <Typography sx={{ fontSize: 11, color: "text.disabled", mb: 2 }}>
              · 口径：答案含本品牌有效提及的探测样本；位次 = 本品牌与已配置竞品
              在答案中的首次出现位置（rank = 1 + 早于本品牌出现的竞品数）；
              情感为逐答案 LLM 判定，无法判定单列「未知」不计入中性
              {posMetrics.competitor_names && posMetrics.competitor_names.length > 0
                ? ` · 对照竞品：${posMetrics.competitor_names.join("、")}`
                : ""}
            </Typography>

            {posMetrics.available && posMetrics.overall ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1.2fr 0.7fr 1fr 1fr 1fr",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                {["粒度", "样本", "Top1 比率", "Top3 比率", "平均排名"].map((h) => (
                  <Typography
                    key={h}
                    sx={{ fontSize: 11, color: "text.secondary", fontWeight: 600 }}
                  >
                    {h}
                  </Typography>
                ))}
                {[
                  ["全量", posMetrics.overall] as const,
                  ...Object.entries(posMetrics.platforms || {}),
                ].map(([label, agg]) => (
                  <Box key={label} sx={{ display: "contents" }}>
                    <Typography sx={{ fontSize: 12 }}>{label}</Typography>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                      {agg.sample_n}
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                      {formatPct(agg.top1_rate)}
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                      {formatPct(agg.top3_rate)}
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                      {formatRank(agg.avg_rank)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}>
                {posMetrics.reason || "位次指标暂无数据"}——
                {posMetrics.available ? "样本不足" : "配置竞品并跑全量探测后产出"}
                ，对应指标以「—」占位
              </Typography>
            )}

            {posMetrics.sentiment && posMetrics.sentiment.total > 0 && (
              <Box
                sx={{
                  mt: 2,
                  pt: 1.5,
                  borderTop: "1px solid",
                  borderColor: "lineSoft",
                }}
              >
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  情感占比（n={posMetrics.sentiment.total}）：正面{" "}
                  {formatPct(posMetrics.sentiment.positive_rate)} · 中性{" "}
                  {formatPct(posMetrics.sentiment.neutral_rate)} · 负面{" "}
                  {formatPct(posMetrics.sentiment.negative_rate)} · 无法判定{" "}
                  {formatPct(posMetrics.sentiment.unknown_rate)}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* 五维对比 + 异动 */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography
                sx={{ fontSize: 14, fontWeight: 500, color: "primary.main", mb: 2 }}
              >
                BAVS 五维对比 · 本期
              </Typography>

              {error ? (
                <EmptyState text={error} />
              ) : items.length === 0 ? (
                <EmptyState
                  text="暂无竞品对比数据"
                  source="由洞悉引擎周期探测产出 · 产出后自动展示"
                />
              ) : visibleDimensions.length === 0 ? (
                <EmptyState
                  text="暂无五维对比明细数据"
                  source="gap_matrix 五维明细由洞悉引擎产出 · 产出后自动展示"
                />
              ) : (
                <>
                  {visibleDimensions.map((dim, idx) => {
                    const isMaxGap = dim === maxGapDimension;
                    return (
                      <Box key={`dim-${dim}`}>
                        {idx > 0 && (
                          <Box
                            sx={{ borderTop: "1px solid", borderColor: "lineSoft", my: 1.5 }}
                          />
                        )}
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
                        >
                          <Typography
                            sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary" }}
                          >
                            {dim}
                          </Typography>
                          {isMaxGap && <Badge label="差距最大" variant="warning" />}
                        </Box>
                        <Box
                          sx={
                            isMaxGap
                              ? {
                                  bgcolor: "semanticBg.warning",
                                  borderRadius: 2,
                                  px: 1,
                                  mx: -1,
                                }
                              : undefined
                          }
                        >
                          <CompareBar
                            name="我方品牌"
                            score={ourScores.get(dim)}
                            color="primary.main"
                          />
                          {items.map((item, i) => {
                            const gm = gapMatrixByItem.get(item.id);
                            const entry = gm?.get(dim);
                            const competitorScore =
                              entry?.competitor_scores?.[item.competitor_name];
                            return (
                              <CompareBar
                                key={item.id}
                                name={item.competitor_name}
                                score={competitorScore}
                                color={RIVAL_BAR_COLORS[i % RIVAL_BAR_COLORS.length]}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}

                  {/* 口径注 */}
                  <Box
                    sx={{
                      mt: 2,
                      pt: 1.5,
                      borderTop: "1px solid",
                      borderColor: "lineSoft",
                    }}
                  >
                    <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
                      · 口径：BAVS = 品牌提及率×25% + 答案份额×25% + 信源引用×20%
                      + 情感占比×15% + 排名位置×15%
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 0.25 }}>
                      · 维度数值来自 gap_matrix（我方 target_score / 竞品
                      competitor_scores）；"—" 表示该方该维度暂无数据；
                      双方皆无数据的维度整行不渲染（展示门禁 §13.3）
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          {/* 竞品异动检测卡（GET /api/competitive/moves，AR3 同源） */}
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Typography
                  sx={{ fontSize: 14, fontWeight: 500, color: "primary.main" }}
                >
                  {moves.length > 0 || movesFailed
                    ? "竞品异动检测"
                    : "竞品对比快照"}
                </Typography>
                {!movesFailed && moveP0 > 0 && (
                  <Badge label={`P0 ×${moveP0}`} variant="error" />
                )}
                {!movesFailed && moveP1 > 0 && (
                  <Badge label={`P1 ×${moveP1}`} variant="warning" />
                )}
              </Box>

              {movesFailed ? (
                <EmptyState
                  text="异动数据暂未就绪"
                  source="与预警引擎 AR3 同源 · 异动端点接入后自动展示"
                />
              ) : moves.length === 0 ? (
                snapshot && snapshot.items.length > 0 ? (
                  /* 单期运行下降级为竞品基线快照：可落地的当前对比，
                     客户再次运行洞察 Agent 后自动切换为异动追踪 */
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {snapshot.our_rate != null && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          px: 2,
                          py: 1.5,
                          border: "1px solid",
                          borderColor: "divider",
                          borderLeft: "3px solid",
                          borderLeftColor: "primary.main",
                          borderRadius: 2,
                          bgcolor: "brand.50",
                        }}
                      >
                        <Badge label="我方" variant="info" />
                        <Typography
                          sx={{ flex: 1, fontSize: 13, color: "text.secondary" }}
                        >
                          <Box
                            component="span"
                            sx={{ fontWeight: 600, color: "text.primary" }}
                          >
                            本品牌
                          </Box>
                          {`——各平台平均提及率 ${snapshot.our_rate.toFixed(1)}%`}
                        </Typography>
                      </Box>
                    )}
                    {snapshot.items.map((s) => (
                      <Box
                        key={s.rival}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          px: 2,
                          py: 1.5,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 2,
                          bgcolor: "background.paper",
                        }}
                      >
                        <Typography
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: 13,
                            fontWeight: 600,
                            color: "text.primary",
                          }}
                        >
                          {s.rival}
                          <Box
                            component="span"
                            sx={{ fontWeight: 400, color: "text.secondary" }}
                          >
                            {`——提及率 ${
                              s.mention_rate != null
                                ? `${s.mention_rate.toFixed(1)}%`
                                : "—"
                            }${
                              s.ranking != null ? ` · 排名 #${s.ranking}` : ""
                            }`}
                          </Box>
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "text.disabled",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatDateTime(s.recorded_at)}
                        </Typography>
                      </Box>
                    ))}
                    <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
                      基线快照：当前各竞品提及率/排名 vs 我方 ·
                      再次运行洞察 Agent 后自动开始追踪异动
                    </Typography>
                  </Box>
                ) : (
                  <EmptyState
                    text="暂无竞品数据"
                    source="运行洞察 Agent 完成竞品探测后自动展示"
                  />
                )
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {moves.map((m, idx) => (
                    <Box
                      key={`${m.rival}-${m.topic}-${m.detected_at}-${idx}`}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        px: 2,
                        py: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                        borderLeft: "3px solid",
                        borderLeftColor:
                          m.level === "P0" ? "error.main" : "warning.main",
                        borderRadius: 2,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Badge
                        label={m.level}
                        variant={m.level === "P0" ? "error" : "warning"}
                      />
                      <Typography
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          fontSize: 13,
                          color: "text.secondary",
                        }}
                      >
                        <Box
                          component="span"
                          sx={{ fontWeight: 600, color: "text.primary" }}
                        >
                          {m.rival}
                          {m.topic ? ` · ${m.topic}` : ""}
                        </Box>
                        {`——${m.platform ? `${m.platform} ` : ""}${
                          MOVE_METRIC_LABELS[m.metric] || m.metric
                        } ${formatMoveValue(m.from)} → ${formatMoveValue(m.to)}`}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: "text.disabled",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatDateTime(m.detected_at)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {moves.length > 0 && (
                <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 1.5 }}>
                  · 异动判定口径与预警引擎 AR3 一致：排名跃升 ≥5 位，或提及率双条件
                  （绝对增量 ≥10 个百分点 且 相对变化 ≥30%）
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* 候选竞品区已移除（展示门禁 §13.3）：候选聚合与确认/忽略动作
              二期接入前无真实数据，整块为占位 → 不渲染。 */}
        </Grid>
      </Grid>
    </Box>
  );
}
